import { toast } from "react-hot-toast";

// ---------------------------------------------------------------------------
// Centralized alert utilities — backed by react-hot-toast.
// A single <Toaster> must be mounted once in App.js.
// ---------------------------------------------------------------------------

const STATUS_MESSAGES = {
  400: "Kërkesa nuk u pranua. Kontrolloni të dhënat dhe provoni përsëri.",
  401: "Sesioni ka skaduar. Ju lutemi kyçuni përsëri.",
  403: "Nuk keni të drejtë për këtë veprim.",
  404: "Të dhënat nuk u gjetën.",
  409: "Veprimi bie në konflikt me të dhënat ekzistuese.",
  422: "Disa të dhëna nuk janë valide. Ju lutemi kontrolloni fushat.",
  429: "Shumë kërkesa në pak kohë. Provoni përsëri pas pak.",
  500: "Ndodhi një problem i brendshëm. Ju lutemi provoni përsëri.",
  502: "Shërbimi është përkohësisht i padisponueshëm. Provoni përsëri.",
  503: "Shërbimi është i mbingarkuar. Provoni përsëri pas pak.",
  504: "Kërkesa mori shumë kohë. Provoni përsëri.",
};

const TECHNICAL_PATTERNS = [
  /stack trace/gi,
  /sqlexception/gi,
  /system\./gi,
  /inner exception/gi,
  /at\s+[\w$.<>]+\s*\(/gi,
  /\b(error|gabim)\s*[:#-]?\s*(400|401|403|404|409|422|429|500|502|503|504)\b/gi,
  /\b(status|http status code)\b\s*[:#-]?\s*\d{3}/gi,
];

const isObject = (value) => value !== null && typeof value === "object";

const sanitizeMessage = (message) => {
  if (typeof message !== "string") return "";

  let cleaned = message.trim();
  if (!cleaned) return "";
  if (cleaned === "[object Object]") return "";

  for (const pattern of TECHNICAL_PATTERNS) {
    cleaned = cleaned.replace(pattern, "").trim();
  }

  cleaned = cleaned.replace(/^(gabim|error)\s*[:\-]?\s*/i, "").trim();
  cleaned = cleaned.replace(/\s{2,}/g, " ").trim();
  return cleaned;
};

const readBackendMessage = (data) => {
  if (!data) return "";

  if (typeof data === "string") {
    return sanitizeMessage(data);
  }

  if (isObject(data)) {
    const direct =
      data.message ||
      data.errorMessage ||
      data.error ||
      data.title ||
      data.detail ||
      "";

    const sanitizedDirect = sanitizeMessage(String(direct || ""));
    if (sanitizedDirect) return sanitizedDirect;

    if (Array.isArray(data.errors) && data.errors.length > 0) {
      const first = sanitizeMessage(String(data.errors[0] || ""));
      if (first) return first;
    }

    if (isObject(data.errors)) {
      const firstKey = Object.keys(data.errors)[0];
      const firstErr = firstKey ? data.errors[firstKey] : null;
      if (Array.isArray(firstErr) && firstErr.length > 0) {
        const first = sanitizeMessage(String(firstErr[0] || ""));
        if (first) return first;
      }
    }
  }

  return "";
};

/** Extract a readable message from an Axios error, with an optional fallback. */
export const getApiErrorMessage = (error, fallbackMessage = "Ndodhi një gabim. Ju lutemi provoni përsëri.") => {
  if (!error) return fallbackMessage;

  const status = Number(error?.response?.status || 0);
  const backendMessage = readBackendMessage(error?.response?.data);

  // If backend message is user-friendly, prefer it.
  if (backendMessage && backendMessage.length >= 3) {
    return backendMessage;
  }

  if (status && STATUS_MESSAGES[status]) {
    return STATUS_MESSAGES[status];
  }

  if (error?.request && !error?.response) {
    return "Nuk u arrit serveri. Kontrolloni internetin dhe provoni përsëri.";
  }

  const genericMessage = sanitizeMessage(String(error?.message || ""));
  if (genericMessage) {
    return genericMessage;
  }

  return fallbackMessage;
};

/** Green toast — use for create / insert operations. */
export const handleSuccess = (text) => {
  toast.success(text, {
    duration: 3000,
    icon: "✅",
    style: {
      background: "#16a34a",
      color: "#ffffff",
      border: "1px solid #15803d",
    },
  });
};



/** Green toast — use for successful delete operations. */
export const deleteAlert = (text) => {
  toast(text, {
    duration: 3500,
    icon: "🗑️",
    style: {
      background: "#f97316",
      color: "#ffffff",
      border: "1px solid #ea580c",
    },
  });
};

/** Red toast — use for all error conditions. */
export const errorAlert = (textOrError, maybeError = null) => {
  let message = "";

  if (maybeError) {
    const fallback = typeof textOrError === "string" ? sanitizeMessage(textOrError) : "";
    message = getApiErrorMessage(maybeError, fallback || undefined);
  } else if (isObject(textOrError) && (textOrError.response || textOrError.request || textOrError.message)) {
    message = getApiErrorMessage(textOrError);
  } else if (typeof textOrError === "string") {
    message = sanitizeMessage(textOrError);
  } else {
    message = "Ndodhi një gabim. Ju lutemi provoni përsëri.";
  }

  toast.error(message || "Ndodhi një gabim. Ju lutemi provoni përsëri.", { duration: 5000 });
};

/** Green toast — use for update / edit operations. */
export const updateAlert = (text) => {
  toast(text, {
    duration: 3000,
    icon: "✏️",
    style: {
      background: "#2563eb",
      color: "#ffffff",
      border: "1px solid #1d4ed8",
    },
  });
};

/** Neutral toast — use for informational messages. */
export const infoAlert = (text) => {
  toast(text, { duration: 4000 });
};

/** Branded notice toast — use for user-facing non-error business notifications. */
export const noticeAlert = (text) => {
  toast(text, {
    duration: 4600,
    icon: "ℹ️",
    style: {
      background: "#e9f1ed",
      color: "#1f3a36",
      border: "1px solid #c6d8cf",
      boxShadow: "0 6px 14px rgba(31, 58, 54, 0.12)",
      fontWeight: "600",
      borderRadius: "10px",
    },
  });
};

/** Warning toast — use for fiscal year changes and other caution messages. */
export const fiscalYearAlert = (text) => {
  toast(text, {
    duration: 5000,
    icon: "⚠️",
    style: { background: "#f59e0b", color: "#fff" },
  });
};


